import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  Timestamp,
  limit,
  docData,
  getDocs
} from '@angular/fire/firestore';
import { BehaviorSubject, Observable, combineLatest, map, switchMap, of } from 'rxjs';
import { Auth, user } from '@angular/fire/auth';
import { getKenyaDateString } from '../utils/date.utils';

export interface Trip {
  id: string;
  routeId: string;
  vehicleId: string;
  fleetNumber?: string;
  assistantId: string;
  schoolId: string;
  type: 'morning' | 'evening' | 'errand';
  startTime: Timestamp;
  endTime: Timestamp | null;
  startMileage: number;
  endMileage: number | null;
  status: 'active' | 'completed';
  date: string;
  students?: {
    total: number;
    onBus: number;
    absent: number;
    parentHandled: number;
  };
}

export interface TripStudent {
  id: string;
  tripId: string;
  studentId: string;
  firstName: string;
  lastName: string;
  stage: string;
  parentPhone: string;
  grade?: string;
  // Completed refers to users cleared from "pending" when Trip is Ended
  status: 'absent' | 'alighted' | 'completed' | 'dropped' | 'parentDropped' | 'parentPicked' | 'pending' | 'onBus';
  pickupTime: Timestamp | null;
  dropoffTime: Timestamp | null;
}

export type TripActivityType = 'morning' | 'evening' | 'errand' | '';


@Injectable({
  providedIn: 'root'
})
export class TripService {

  private activitySource = new BehaviorSubject<TripActivityType>('');
  currentActivity$ = this.activitySource.asObservable();

  setActivity(activity: TripActivityType) {
    this.activitySource.next(activity);
  }

  constructor(
    private firestore: Firestore,
    private auth: Auth
  ) { }

  getTripObjectiveById(objId: string) {
    const objectivesRef = doc(this.firestore, 'tripObjectives', objId);
    return docData(objectivesRef);
  }

  getTripObjectives() {
    const objectivesRef = collection(this.firestore, 'tripObjectives');
    const objRefQuery = query(
      objectivesRef,
      orderBy('name', 'asc')
    );

    return collectionData(objRefQuery);
  }

  // Trip Management
  async startTrip(
    routeId: string,
    vehicleId: string,
    type: TripActivityType,
    startMileage: number,
    userId: string,
    schoolId: string,
    fleetNumber?: string,
    assistantName?: string
  ) {
    // Automatically close any duplicate or orphaned active trips for this assistant
    if (userId) {
      try {
        const activeTripsSnap = await getDocs(
          query(
            collection(this.firestore, 'trips'),
            where('assistantId', '==', userId),
            where('status', '==', 'active')
          )
        );
        for (const tDoc of activeTripsSnap.docs) {
          await updateDoc(tDoc.ref, {
            status: 'completed',
            endTime: serverTimestamp(),
            endMileage: startMileage,
            endTripBy: userId
          });
        }
      } catch (err) {
        console.warn('Could not auto-resolve previous active trips:', err);
      }
    }

    const tripData: any = {
      routeId,
      vehicleId,
      assistantId: userId,
      schoolId,
      type,
      startTime: serverTimestamp(),
      startMileage,
      status: 'active',
      date: getKenyaDateString(),
      endTime: null,
      endMileage: null
    };

    if (fleetNumber) {
      tripData.fleetNumber = fleetNumber;
    }
    if (assistantName) {
      tripData.assistantName = assistantName;
    }

    return addDoc(collection(this.firestore, 'trips'), tripData);
  }

  async endTrip(
    tripId: string,
    endMileage: number,
    endTripBy: string,
    options?: {
      vehicleId?: string;
      students?: {
        total: number;
        onBus: number;
        absent: number;
        parentHandled: number;
      };
    }
  ) {
    const tripRef = doc(this.firestore, 'trips', tripId);
    const updatePayload: any = {
      endTime: serverTimestamp(),
      endTripBy: endTripBy || '',
      endMileage: Number(endMileage),
      status: 'completed'
    };

    if (options?.students) {
      updatePayload.students = options.students;
    }

    await updateDoc(tripRef, updatePayload);

    // Also clean up any duplicate active trips for this assistant so no ghost trip remains
    if (endTripBy) {
      try {
        const activeTripsSnap = await getDocs(
          query(
            collection(this.firestore, 'trips'),
            where('assistantId', '==', endTripBy),
            where('status', '==', 'active')
          )
        );
        for (const tDoc of activeTripsSnap.docs) {
          if (tDoc.id !== tripId) {
            await updateDoc(tDoc.ref, {
              endTime: serverTimestamp(),
              endTripBy: endTripBy,
              endMileage: Number(endMileage),
              status: 'completed'
            });
          }
        }
      } catch (err) {
        console.warn('Could not auto-close duplicate active trips on endTrip:', err);
      }
    }

    // Sync mileage to vehicle document for fleet and future trips
    if (options?.vehicleId && endMileage) {
      try {
        const vehicleRef = doc(this.firestore, 'vehicles', options.vehicleId);
        await updateDoc(vehicleRef, {
          lastMileage: endMileage,
          mileageRef: new Date().toISOString()
        });
      } catch (err) {
        console.warn('Vehicle mileage update non-critical warning:', err);
      }
    }
  }

  // Student Management during Trip
  updateStudentStatus(
    tripStudentId: string,
    tripId: string,
    studentId: string,
    status: TripStudent['status']
  ) {
    return updateDoc(doc(this.firestore, 'tripStudents', tripStudentId), {
      tripId,
      studentId,
      status,
      pickupTime: serverTimestamp(),
      dropoffTime: null
    });
  }

  dropOffStudent(tripStudentId: string) {
    const studentRef = doc(this.firestore, 'tripStudents', tripStudentId);
    return updateDoc(studentRef, {
      status: 'dropped',
      dropoffTime: serverTimestamp()
    });
  }

  getActiveTripIdForLoggedInUser(): Observable<string | null> {
    return user(this.auth).pipe(
      switchMap(currentUser => currentUser ? this.getActiveTrip(currentUser.uid) : of(null)),
      map(trip => trip?.id || null)
    );
  }

  // Queries
  getActiveTrip(assistantId: string): Observable<Trip | null> {
    const tripsRef = collection(this.firestore, 'trips');
    const activeTripsQuery = query(
      tripsRef,
      where('assistantId', '==', assistantId),
      where('status', '==', 'active'),
      orderBy('startTime', 'desc'),
      limit(1)
    );

    return collectionData(activeTripsQuery, { idField: 'id' }).pipe(
      map(trips => {
        const trip = trips[0] as Trip || null;
        if (trip) {
          trip.id = trips[0].id;
        }
        return trip;
      })
    );
  }

  getTripStudents(tripId: string): Observable<TripStudent[]> {
    const studentsRef = collection(this.firestore, 'tripStudents');
    const tripStudentsQuery = query(
      studentsRef,
      where('tripId', '==', tripId),
      orderBy('pickupTime', 'asc')
    );

    return collectionData(tripStudentsQuery, { idField: 'id' }) as Observable<TripStudent[]>;
  }

  // Reports
  getDailyTrips(date: string, schoolId: string): Observable<Trip[]> {
    const tripsRef = collection(this.firestore, 'trips');
    const dailyTripsQuery = query(
      tripsRef,
      where('date', '==', date),
      where('schoolId', '==', schoolId),
      orderBy('startTime', 'asc')
    );

    return collectionData(dailyTripsQuery, { idField: 'id' }) as Observable<Trip[]>;
  }

  getDailyTripsForAssistant(date: string, assistantId: string): Observable<Trip[]> {
    const tripsRef = collection(this.firestore, 'trips');
    const assistantTripsQuery = query(
      tripsRef,
      where('date', '==', date),
      where('assistantId', '==', assistantId)
    );

    return (collectionData(assistantTripsQuery, { idField: 'id' }) as Observable<Trip[]>).pipe(
      map(trips => (trips || []).sort((a, b) => {
        const timeA = a.startTime ? (a.startTime as any).toMillis?.() || 0 : 0;
        const timeB = b.startTime ? (b.startTime as any).toMillis?.() || 0 : 0;
        return timeA - timeB;
      }))
    );
  }

  getTripsById(tripIds: string[]) {
    return collectionData(
      query(
        collection(this.firestore, 'trips'),
        where('__name__', 'in', tripIds)  // Changed from 'id' to '__name__'
      ),
      { idField: 'id' }  // Added idField option
    ) as Observable<Trip[]>;
  }

  getStudentTripHistory(
    studentId: string,
    startDate: string,
    endDate: string
  ): Observable<TripStudent[]> {
    const studentsRef = collection(this.firestore, 'tripStudents');
    // Convert start date to timestamp (beginning of day)
    const startTimestamp = Timestamp.fromDate(new Date(startDate + 'T00:00:00'));
    // Convert end date to timestamp (end of day)
    const endTimestamp = Timestamp.fromDate(new Date(endDate + 'T23:59:59'));

    const historyQuery = query(
      studentsRef,
      where('studentId', '==', studentId),
      where('pickupTime', '>=', startTimestamp),
      where('pickupTime', '<=', endTimestamp),
      orderBy('pickupTime', 'desc')
    );

    return collectionData(historyQuery, { idField: 'id' }) as Observable<TripStudent[]>;
  }

  getRoutePerformance(
    routeId: string,
    startDate: string,
    endDate: string
  ): Observable<any> {
    const tripsRef = collection(this.firestore, 'trips');
    const routeQuery = query(
      tripsRef,
      where('routeId', '==', routeId),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      where('status', '==', 'completed')
    );

    return collectionData(routeQuery, { idField: 'id' }).pipe(
      map(trips => {
        const stats = trips.reduce((acc: any, trip: any) => {
          const duration = trip.endTime - trip.startTime;
          const mileage = trip.endMileage - trip.startMileage;

          return {
            totalTrips: acc.totalTrips + 1,
            avgDuration: (acc.avgDuration * acc.totalTrips + duration) / (acc.totalTrips + 1),
            avgMileage: (acc.avgMileage * acc.totalTrips + mileage) / (acc.totalTrips + 1),
            maxDuration: Math.max(acc.maxDuration, duration),
            minDuration: Math.min(acc.minDuration, duration)
          };
        }, {
          totalTrips: 0,
          avgDuration: 0,
          avgMileage: 0,
          maxDuration: 0,
          minDuration: Infinity
        });

        return {
          routeId,
          ...stats
        };
      })
    );
  }

  // Alert Monitoring
  monitorLateTrips(schoolId: string): Observable<Trip[]> {
    const morningCutoff = new Date();
    morningCutoff.setHours(8, 30, 0); // 8:30 AM

    const eveningCutoff = new Date();
    eveningCutoff.setHours(19, 0, 0); // 7:00 PM

    const activeTripsQuery = query(
      collection(this.firestore, 'trips'),
      where('schoolId', '==', schoolId),
      where('status', '==', 'active'),
      where('startTime', '<=', Timestamp.fromDate(morningCutoff))
    );

    return collectionData(activeTripsQuery, { idField: 'id' }).pipe(
      map(trips => trips.filter(trip => {
        const cutoffTime = trip.type === 'morning' ? morningCutoff : eveningCutoff;
        return new Date() > cutoffTime;
      }))
    ) as Observable<Trip[]>;
  }

  // Utility function to get trip details with related data
  getTripDetails(tripId: string): Observable<any> {
    const trip$ = docData(
      doc(this.firestore, 'trips', tripId),
      { idField: 'id' }
    );

    const students$ = this.getTripStudents(tripId);

    return combineLatest([trip$, students$]).pipe(
      map(([trip, students]) => ({
        ...trip,
        students: {
          onBus: students.filter(s => s.status === 'onBus'),
          absent: students.filter(s => s.status === 'absent'),
          parentHandled: students.filter(s =>
            s.status === 'parentDropped' || s.status === 'parentPicked'
          )
        }
      }))
    );
  }

}
